/**
 * DataHealthBanner — the provenance strip pinned to the TOP of the content area (DISP-02 §6.1). Consumes
 * GET /api/v1/data-health and states the feed health honestly: LIVE when every feed is real, otherwise
 * MOCK / awaiting-real-extract with a count. Its whole job is to prevent the "empty implies zero"
 * clinical misread, so it explicitly says feed-gated surfaces are not zero. Rendered SLIM (single-line,
 * expandable) so it reads as a strip — not a block that dominates the layout. Renders only counts and
 * model-agnostic copy: NEVER logs rows, never renders PHI.
 */
import { useEffect, useState, type ReactNode } from "react";
import { SkyMessageBar } from "@skypointcloud/skypoint-ui";
import type { DataHealthRow } from "@shared/contracts";
import { fetchEnvelope } from "../lib/api";
import { dataHealthRowsSchema } from "../lib/schemas/dataHealth";

/**
 * P1-6 (a11y): the banner's resolved/failed/warning/success state is a meaningful status change for AT, but
 * an SPA swaps it silently. Wrap it in a PERSISTENT aria-live="polite" / role="status" region (mounted from
 * first render, even while empty) so each state transition is announced. The live region carries no PHI:
 * the banner copy is model-agnostic counts only, never rows.
 */
function ProvenanceLiveRegion({ children }: { children: ReactNode }) {
  return (
    <div role="status" aria-live="polite" aria-label="Data health">
      {children}
    </div>
  );
}

export function DataHealthBanner() {
  const [rows, setRows] = useState<DataHealthRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchEnvelope<DataHealthRow[]>("/api/v1/data-health", undefined, dataHealthRowsSchema)
      .then(({ data }) => {
        if (cancelled) return;
        // H1 + drift guard: a 200 whose body drifted to a non-array must NOT reach rows.filter() (which
        // would throw and, since this strip renders above the route boundary, take down the whole shell).
        // Treat a non-array like a failed check: degrade to the explicit "unavailable" state, never a
        // false-green and never a crash.
        if (Array.isArray(data)) setRows(data);
        else setFailed(true);
      })
      .catch(() => {
        // H1: a failed fetch must NEVER read as the green "0 LIVE, 0 pending" success state. Surface an
        // explicit neutral/warning "unavailable" banner; never a stack/PHI, never a false-green.
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // H1: fetch error -> explicit unavailable banner (warning), not a false-green success.
  if (failed) {
    return (
      <ProvenanceLiveRegion>
        <SkyMessageBar
          banner
          layout="singleline"
          expandable
          intent="warning"
          title="Data feeds"
          message="We could not check data feed status right now, so it is temporarily unavailable. Any empty space below means information is still on its way, not a real zero."
        />
      </ProvenanceLiveRegion>
    );
  }

  // Not loaded yet -> render the EMPTY live region (no visible banner, no flicker) so the region is present
  // before content arrives and the first resolved state is announced as a change.
  if (!rows) return <ProvenanceLiveRegion>{null}</ProvenanceLiveRegion>;

  // H1: an empty manifest is UNKNOWN, not "all live". Never resolve intent to success on 0 rows.
  if (rows.length === 0) {
    return (
      <ProvenanceLiveRegion>
        <SkyMessageBar
          banner
          layout="singleline"
          expandable
          intent="warning"
          title="Data feeds"
          message="No data feeds reported their status, so feed status is unknown. Any empty space below means information is still on its way, not a real zero."
        />
      </ProvenanceLiveRegion>
    );
  }

  const live = rows.filter((r) => r.data_status === "real").length;
  const pending = rows.length - live;
  const intent = pending === 0 ? "success" : pending > live ? "warning" : "info";
  const message =
    pending === 0
      ? `Data feeds: all ${live} live.`
      : `Data feeds: ${live} live, ${pending} still loading. An empty space below means data is on its way, not a real zero.`;

  return (
    <ProvenanceLiveRegion>
      <SkyMessageBar
        banner
        layout="singleline"
        expandable
        intent={intent}
        title="Data feeds"
        message={message}
      />
    </ProvenanceLiveRegion>
  );
}
